import { ModelSetTransformer, ModelTransformer } from '../common/common.model';

export interface ExtTdbfgComplianceDetailsPopupItem {
    pillarID: number;
    categoryID: number;
    pillarName: string;
    categoryName: string;
    categoryScore: number;
    categoryScoreColour: string;
    oppName: string;
    numberOfOpps: number;
}

export interface TdbfgComplianceDetailsPopupOpp {
    name: string;
    number: number;
}

export interface TdbfgComplianceDetailsPopupCategory {
    name: string;
    score: number;
    scoreColor: string;
    opps: TdbfgComplianceDetailsPopupOpp[];
}

interface IntTdbfgComplianceDetailsPopupCategory {
    items: ExtTdbfgComplianceDetailsPopupItem[];
}

export interface TdbfgComplianceDetailsPopupItem {
    name: string;
    categories: TdbfgComplianceDetailsPopupCategory[];
}

interface IntTdbfgComplianceDetailsPopupPillar {
    items: ExtTdbfgComplianceDetailsPopupItem[];
}

export interface TdbfgComplianceDetailsPopup {
    items: TdbfgComplianceDetailsPopupItem[];
}

class TdbfgComplianceDetailsPopupOppModelTransformer
    implements
        ModelTransformer<
            TdbfgComplianceDetailsPopupOpp,
            ExtTdbfgComplianceDetailsPopupItem
        >
{
    transform(
        model: ExtTdbfgComplianceDetailsPopupItem,
    ): TdbfgComplianceDetailsPopupOpp {
        return {
            name: model.oppName,
            number: model.numberOfOpps,
        };
    }
}

class TdbfgComplianceDetailsPopupOppModelSetTransformer extends ModelSetTransformer<
    TdbfgComplianceDetailsPopupOpp,
    ExtTdbfgComplianceDetailsPopupItem
> {
    protected newTransformer(): ModelTransformer<
        TdbfgComplianceDetailsPopupOpp,
        ExtTdbfgComplianceDetailsPopupItem
    > {
        return new TdbfgComplianceDetailsPopupOppModelTransformer();
    }
}

class TdbfgComplianceDetailsPopupCategoryModelTransformer
    implements
        ModelTransformer<
            TdbfgComplianceDetailsPopupCategory,
            IntTdbfgComplianceDetailsPopupCategory
        >
{
    transform(
        model: IntTdbfgComplianceDetailsPopupCategory,
    ): TdbfgComplianceDetailsPopupCategory {
        const opps_ = model.items.filter(
            (value) =>
                !(
                    value.categoryScore == null &&
                    value.categoryScoreColour == null &&
                    value.numberOfOpps == null &&
                    value.oppName == null
                ),
        );
        const opps =
            new TdbfgComplianceDetailsPopupOppModelSetTransformer().transform(
                opps_,
            );
        return {
            name: model.items[0].categoryName,
            score:
                model.items[0].categoryScore == null
                    ? null
                    : model.items[0].categoryScore / 100,
            scoreColor: model.items[0].categoryScoreColour,
            opps,
        };
    }
}

class TdbfgComplianceDetailsPopupCategoryModelSetTransformer extends ModelSetTransformer<
    TdbfgComplianceDetailsPopupCategory,
    IntTdbfgComplianceDetailsPopupCategory
> {
    protected newTransformer(): ModelTransformer<
        TdbfgComplianceDetailsPopupCategory,
        IntTdbfgComplianceDetailsPopupCategory
    > {
        return new TdbfgComplianceDetailsPopupCategoryModelTransformer();
    }
}

class TdbfgComplianceDetailsPopupItemModelTransformer
    implements
        ModelTransformer<
            TdbfgComplianceDetailsPopupItem,
            IntTdbfgComplianceDetailsPopupPillar
        >
{
    transform(
        model: IntTdbfgComplianceDetailsPopupPillar,
    ): TdbfgComplianceDetailsPopupItem {
        const categoriesIds = [];
        const categories_: IntTdbfgComplianceDetailsPopupCategory[] = [];
        model.items.forEach((value) => {
            const index = categoriesIds.indexOf(value.categoryID);
            if (index !== -1) {
                categories_[index].items.push(value);
                return;
            }
            categoriesIds.push(value.categoryID);
            categories_.push({
                items: [value],
            });
        });

        const categories =
            new TdbfgComplianceDetailsPopupCategoryModelSetTransformer().transform(
                categories_,
            );
        return {
            name: model.items[0].pillarName,
            categories,
        };
    }
}

class TdbfgComplianceDetailsPopupItemModelSetTransformer extends ModelSetTransformer<
    TdbfgComplianceDetailsPopupItem,
    IntTdbfgComplianceDetailsPopupPillar
> {
    protected newTransformer(): ModelTransformer<
        TdbfgComplianceDetailsPopupItem,
        IntTdbfgComplianceDetailsPopupPillar
    > {
        return new TdbfgComplianceDetailsPopupItemModelTransformer();
    }
}

export class TdbfgComplianceDetailsPopupModelTransformer
    implements
        ModelTransformer<
            TdbfgComplianceDetailsPopup,
            ExtTdbfgComplianceDetailsPopupItem[]
        >
{
    transform(
        model: ExtTdbfgComplianceDetailsPopupItem[],
    ): TdbfgComplianceDetailsPopup {
        const pillarsIds = [];
        const pillars_: IntTdbfgComplianceDetailsPopupPillar[] = [];
        model.forEach((value) => {
            const index = pillarsIds.indexOf(value.pillarID);
            if (index !== -1) {
                pillars_[index].items.push(value);
                return;
            }
            pillarsIds.push(value.pillarID);
            pillars_.push({
                items: [value],
            });
        });

        const items =
            new TdbfgComplianceDetailsPopupItemModelSetTransformer().transform(
                pillars_,
            );
        return {
            items,
        };
    }
}

export function validateTdbfgComplianceDetailsPopup(
    model: TdbfgComplianceDetailsPopup,
): void {
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    const valid =
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        model != null &&
        model.items != null &&
        model.items.length !== 0 &&
        model.items.every(
            (i) =>
                i.categories != null &&
                !i.categories.every(
                    (c) =>
                        c.score == null &&
                        (c.opps == null || c.opps.length === 0),
                ),
        );
    if (!valid) {
        throw new Error('TdbfgComplianceDetailsPopup not valid');
    }
}
